<template>
  <div class="media-wrapper">
    <!-- Toggle button to collapse/expand overlay -->
    <button class="toggle-overlay-btn" :style="{ right: overlayVisible ? '260px' : '10px' }" @click="toggleOverlay">
      <span v-if="overlayVisible">⮜</span>
      <span v-else>⮞</span>
    </button>

    <!-- Primary large view for focused item -->
    <div class="primary-view">
      <iframe
        v-if="focusedItem && focusedItem.type === 'webpage'"
        :src="focusedItem.src"
        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
        allowfullscreen
      ></iframe>
      <video
        v-else-if="focusedItem"
        :src="focusedItem.src"
        controls
        autoplay
      ></video>
    </div>

    <!-- Collapsible overlay menu on the right showing tiles or initials -->
    <div
      class="video-grid-overlay"
      :class="{ collapsed: !overlayVisible }"
    >
      <!-- Full view: tiles -->
      <template v-if="overlayVisible">
        <div
          v-for="(item, index) in mediaItems"
          :key="index"
          class="video-tile"
          ref="tiles"
          @click="focusItem(item)"
          v-bind="draggables[index]"
        >
          <iframe
            v-if="item.type === 'webpage'"
            :src="item.src"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            allowfullscreen
          ></iframe>
          <video
            v-else
            :src="item.src"
            muted
          ></video>

          <div class="controls-overlay">
            <button @click.stop="reload(item, index)">🔄</button>
          </div>
        </div>
      </template>
      <!-- Collapsed view: initials -->
      <template v-else>
        <div
          v-for="(item, index) in mediaItems"
          :key="index"
          class="initial-tile"
          @click="focusItem(item); toggleOverlay()"
        >
          {{ item.src.match(/https?:\/\/(www\.)?([^\/]+)/)[2].split('.')
            .map(part => part[0].toUpperCase()).join('') }}
        </div>
      </template>
    </div>

    <!-- Global controls -->
    <div class="global-controls">
      <button @click="reloadAll">Reload All</button>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue';
import { useDraggable } from '@vueuse/core';

export default {
  name: 'MediaGrid',
  setup() {
    const inputs = [
      { src: 'https://www.wikipedia.org/', type: 'webpage' },
      { src: 'https://www.w3schools.com/html/movie.mp4', type: 'videoFile' },
      { src: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4', type: 'stream' }
    ];

    const mediaItems = ref(inputs);
    const focusedItem = ref(inputs[0]);
    const overlayVisible = ref(true);

    const tiles = ref([]);
    const draggables = ref([]);

    onMounted(() => {
      draggables.value = tiles.value.map(tileEl => useDraggable(tileEl));
    });

    const reload = (item, index) => {
      const tileEl = tiles.value[index];
      const el = tileEl.querySelector(item.type === 'webpage' ? 'iframe' : 'video');
      if (item.type === 'webpage') el.src = el.src;
      else { el.currentTime = 0; el.play(); }
    };

    const reloadAll = () => {
      tiles.value.forEach((tileEl, idx) => {
        const item = mediaItems.value[idx];
        const el = tileEl.querySelector(item.type === 'webpage' ? 'iframe' : 'video');
        if (item.type === 'webpage') el.src = el.src;
        else { el.currentTime = 0; el.play(); }
      });
    };

    const focusItem = (item) => { focusedItem.value = item; };

    const toggleOverlay = () => { overlayVisible.value = !overlayVisible.value; };

    return {
      mediaItems,
      focusedItem,
      overlayVisible,
      tiles,
      draggables,
      reload,
      reloadAll,
      focusItem,
      toggleOverlay
    };
  }
};
</script>

<style scoped>
.media-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: white;
  position: relative;
}

/* Toggle button styling */
.toggle-overlay-btn {
  position: absolute;
  top: 47px;
  right: 260px;
  z-index: 4;
  background: #333;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 3px 6px;
  cursor: pointer;
}

.primary-view {
  flex: 1;
  position: relative;
  background: #eee;
  overflow: hidden;
}

.primary-view iframe,
.primary-view video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border: none;
}

.video-grid-overlay {
  position: absolute;
  top: 0;
  right: 0;
  width: 250px;
  height: 100%;
  background: rgba(255,255,255,0.95);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 10px;
  box-shadow: -2px 0 8px rgba(0,0,0,0.1);
  z-index: 2;
  transition: width 0.3s ease, padding 0.3s ease;
}

/* Collapsed state */
.video-grid-overlay.collapsed {
  width: 40px;
  padding: 5px;
}

.video-tile {
  position: relative;
  width: 100%;
  height: 120px;
  margin-bottom: 10px;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: transform 0.2s;
}

.video-tile:hover { transform: scale(1.03); }

.video-tile iframe,
.video-tile video { width: 100%; height: 100%; object-fit: cover; }

/* Initial tiles styling */
.initial-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  margin-bottom: 5px;
  border: 1px solid #ccc;
  border-radius: 8px;
  background: #f7f7f7;
  color: #333;
  font-weight: bold;
  cursor: pointer;
}

.controls-overlay {
  position: absolute;
  bottom: 5px;
  right: 5px;
  display: flex;
  gap: 5px;
  background: rgba(0,0,0,0.5);
  padding: 4px;
  border-radius: 4px;
}

.controls-overlay button {
  background: none;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
}

.global-controls {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: #f0f0f0;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  z-index: 3;
}

.global-controls button {
  padding: 6px 12px;
  background: #333;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>
